import type { TFunction } from 'react-i18next'
import { useTranslation } from 'react-i18next'

import { DelayedSpinner } from '@cdab/scania/qpr/components/atoms'
import {
  AuditCertifyError,
  type Audit,
  type Dealer
} from '@cdab/scania/qpr/schema'

import { ActionsSection } from './audit-summary.styles'

import {
  useActionPlanReminder,
  useAuditReminder,
  useCertifyAudit
} from '@cdab/scania/qpr/hooks'
import { capitalizeFirstLetter } from '@cdab/utils'
import { TdsBlock, TdsButton, TdsMessage, TdsToast } from '@scania/tegel-react'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

export interface AuditActionsProps {
  dealerId: Dealer['id']
  auditId: Audit['id']
  canCertify: boolean
  className?: string
  hasDeviations: boolean
  isCertified: boolean
}

function getErrorMessage(error: string | undefined, t: TFunction) {
  switch (error) {
    case AuditCertifyError.AuditIsArchived: {
      return capitalizeFirstLetter(t('certify.audit-is-archived'))
    }
    case AuditCertifyError.DealerIsInvalid: {
      return capitalizeFirstLetter(t('certify.dealer-is-invalid'))
    }
    case AuditCertifyError.UnfilledDevelopmentPoints: {
      return t('certify.unfilled-development-points')
    }
    case AuditCertifyError.UnfulfilledMandatoryPoints: {
      return t('certify.unfulfilled-mandatory-points')
    }
    case AuditCertifyError.UnfulfilledDeviations: {
      return t('certify.unfulfilled-deviations')
    }
    case AuditCertifyError.NotValidAuditType: {
      return t('certify.not-valid-audit-type')
    }
    default: {
      return ''
    }
  }
}

export function AuditActions({
  auditId,
  canCertify,
  dealerId,
  hasDeviations,
  isCertified
}: AuditActionsProps) {
  const { t } = useTranslation(['audit', 'common'])

  const [stateCertify, allowedToCertify, handleClickCertify, disableCertify] =
    useCertifyAudit(auditId, canCertify, dealerId)

  const [stateAuditReminder, handleClickAuditReminder] =
    useAuditReminder(auditId)

  const [stateActionPlanReminder, handleClickActionPlanReminder] =
    useActionPlanReminder(auditId)

  const certifyButtonVisible =
    canCertify && allowedToCertify && !stateCertify.matches('success')
  const auditReminderButtonVisible = isCertified
  const actionPlanReminderButtonVisible = hasDeviations

  useEffect(() => {
    if (stateCertify.matches('success')) {
      toast(
        <TdsToast
          variant='success'
          header={capitalizeFirstLetter(t('certify.success-certified'))}
        />
      )
      disableCertify()
    }
  }, [auditId, dealerId, disableCertify, stateCertify, t])

  if (
    !certifyButtonVisible &&
    !auditReminderButtonVisible &&
    !actionPlanReminderButtonVisible
  ) {
    return null
  }

  return (
    <TdsBlock modeVariant='secondary' className='tds-u-mt3 '>
      <h6 className='tds-headline-06 tds-u-m0'>
        {capitalizeFirstLetter(t('actions', { ns: 'common' }))}
      </h6>
      <ActionsSection>
        {certifyButtonVisible && (
          <TdsButton
            variant='primary'
            onClick={handleClickCertify}
            disabled={!['enabled', 'error'].some(stateCertify.matches)}
            text={capitalizeFirstLetter(t('certify', { ns: 'common' }))}
          />
        )}
        {stateCertify.matches('loading') && <DelayedSpinner />}
        {auditReminderButtonVisible && (
          <TdsButton
            variant='primary'
            disabled={stateAuditReminder.status === 'loading'}
            onClick={handleClickAuditReminder}
            text={capitalizeFirstLetter(
              t('reminders.audit-reminder', { ns: 'audit' })
            )}
          />
        )}

        {actionPlanReminderButtonVisible && (
          <TdsButton
            variant='primary'
            disabled={stateActionPlanReminder.status === 'loading'}
            onClick={handleClickActionPlanReminder}
            text={capitalizeFirstLetter(
              t('reminders.action-plan-reminder', { ns: 'audit' })
            )}
          />
        )}
      </ActionsSection>
      {stateCertify.matches('error') && (
        <div className='tds-u-mt2'>
          <TdsMessage
            header={getErrorMessage(stateCertify.context.error, t)}
            variant={'error'}
          />
        </div>
      )}
      {(stateActionPlanReminder.status === 'error' ||
        stateActionPlanReminder.status === 'success') && (
        <div className='tds-u-mt2'>
          <TdsMessage
            header={
              stateActionPlanReminder.status === 'success'
                ? t('reminders.audit-action-plan-reminder-success', {
                    ns: 'audit'
                  })
                : stateActionPlanReminder.message
            }
            variant={stateActionPlanReminder.status}
          />
        </div>
      )}
    </TdsBlock>
  )
}
